import { ActionType, createAsyncAction, createAction } from 'typesafe-actions';
import { FetchAction, SuccessFetchActionPayload, DefaultErrorPayload } from '../dataFetching/generators';
import { DataFetchingThunkAction } from '../types';
import { log404ErrorAsWarning } from '../../../common/apiAdapters/ErrorHandlers';
import { PriceFormatConfigOptions } from '../products/types';
import { addNotification } from '../notifications/actions';
import { remoteUpdateCartEvent } from '../../../common/events/service';
import { getAddonProductsUrl } from '../../urlService';
import { ProductDetail } from '../productdetail/types';
import { AddToCartParams } from '../../../common/apiAdapters/types';
import { splitAsyncAction } from '../utils';
import { AddToCartResponse, CartCountResponse, CartResponse, CLEAR_CART, CLEAR_CART_DATA, MARK_FETCHED } from './types';
import { NavigationPush } from 'Common/appshell_types';
import GaTracking from 'Common/ga';
import retry from 'Common/utils/retry';

export const fetchCartCountAction = createAsyncAction(
    '@CART_DATA/FETCH_START',
    '@CART_DATA/FETCH_SUCCESS',
    '@CART_DATA/FETCH_FAILED'
)<FetchAction, SuccessFetchActionPayload<CartCountResponse>, DefaultErrorPayload>();

export const fetchCartCount = (controller?: AbortController, noRetry = false): DataFetchingThunkAction => async (
    dispatch,
    _,
    { apiService, logger }
) => {
    const identifier = 'CART_COUNT';
    dispatch(fetchCartCountAction.request({ identifier }));
    const repeatTimes = noRetry ? 0 : 0;
    try {
        const getCartCount = retry((ctrllr?: AbortController) => apiService.shopApi.getCartCount(ctrllr), repeatTimes);
        const cartCount = await getCartCount(controller);
        dispatch(fetchCartCountAction.success({ data: cartCount, identifier }));
    } catch (err) {
        log404ErrorAsWarning(err, identifier, logger, 'fetchCartCount');
        dispatch(fetchCartCountAction.failure({ error: err, identifier }));
    }
};

export const addToCartAction = createAsyncAction(
    '@CART_DATA_ADD_TO_CART/FETCH_START',
    '@CART_DATA_ADD_TO_CART/FETCH_SUCCESS',
    '@CART_DATA_ADD_TO_CART/FETCH_FAILED'
)<FetchAction, SuccessFetchActionPayload<{}>, DefaultErrorPayload>();

export const fetchCartDataAction = createAsyncAction('@CART/FETCH_START', '@CART/FETCH_SUCCESS', '@CART/FETCH_FAILED')<
    FetchAction,
    SuccessFetchActionPayload<CartResponse>,
    DefaultErrorPayload
>();

export const fetchCartData = (reducerPayloadOptions: PriceFormatConfigOptions): DataFetchingThunkAction => async (
    dispatch,
    _,
    { apiService, logger }
) => {
    const identifier = 'CART_DATA';
    dispatch(fetchCartDataAction.request({ identifier }));
    try {
        const cartCount = await apiService.shopApi.getCartData();
        dispatch(fetchCartDataAction.success({ data: cartCount, identifier, reducerPayloadOptions }));
    } catch (err) {
        log404ErrorAsWarning(err, identifier, logger, 'fetchCartData');
        dispatch(fetchCartDataAction.failure({ error: err, identifier }));
    }
};

export const clearCartAction = createAction(CLEAR_CART);
export const clearCartDataAction = createAction(CLEAR_CART_DATA);
export const markCountFetched = createAction(MARK_FETCHED);

export interface AddToCartOptions {
    shouldShowCartFlyout?: boolean;
    successNotification?: string;
    errorNotification: string;
    product: ProductDetail;
    quantity: number;
    simpleSku: string;
    enhancedRecommendations: EnhancedRecommendationsOptions;
}

export interface EnhancedRecommendationsOptions {
    areEnabled: boolean;
    shouldBeShown: boolean;
}

const hasAddOnLink = (cartResponse: AddToCartResponse) => {
    return !!cartResponse.data.links?.some(link => link.rel === 'add_on_products' && link.href);
};

const trackEnhancedRecommendationsExperiment = (
    {
        product,
        cartResponse,
        enhancedRecommendations,
    }: {
        product: ProductDetail;
        cartResponse: AddToCartResponse;
        enhancedRecommendations: EnhancedRecommendationsOptions;
    },
    ga: GaTracking | undefined
) => {
    if (
        ga !== undefined &&
        enhancedRecommendations.areEnabled &&
        hasAddOnLink(cartResponse) &&
        !product.hasAddonProducts // this condition ensures that we don't track experiment when manual recommendations are present
    ) {
        ga.trackExperiment({
            action: 'p100ShowEnhancedCrossSaleRecommendations',
            label: enhancedRecommendations.shouldBeShown.toString(),
        });
    }
};

const shouldRedirectToRecommendationsPage = (
    product: ProductDetail,
    cartResponse: AddToCartResponse,
    enhancedRecommendations: EnhancedRecommendationsOptions
) => {
    return (
        product.hasAddonProducts ||
        (hasAddOnLink(cartResponse) && enhancedRecommendations.areEnabled && enhancedRecommendations.shouldBeShown)
    );
};

export const addToCart = (navigationPush: NavigationPush, options: AddToCartOptions): DataFetchingThunkAction => async (
    dispatch,
    _,
    { apiService, logger, ga }
) => {
    const updateCart = remoteUpdateCartEvent();
    const { product, quantity, simpleSku, enhancedRecommendations } = options;
    const identifier = simpleSku;

    const addToCartParams: AddToCartParams = {
        enhancedRecommendations: enhancedRecommendations.areEnabled ? 1 : 0,
        simplesku: simpleSku,
        quantity,
        addAssemblyService: product?.assemblyServiceProduct?.selected || false,
    };

    try {
        dispatch(addToCartAction.request({ identifier }));
        const cartResponse = await apiService.shopApi.addToCart(addToCartParams);
        dispatch(addToCartAction.success({ data: {}, identifier }));

        trackEnhancedRecommendationsExperiment({ product, cartResponse, enhancedRecommendations }, ga);

        if (shouldRedirectToRecommendationsPage(product, cartResponse, enhancedRecommendations)) {
            navigationPush(getAddonProductsUrl(product.sku, simpleSku));
            return;
        }

        // The preferable way to communicate with the One Header (Micro Frontend) is remote events. We should treat it as a separate application.
        updateCart(!!options.shouldShowCartFlyout);
        if (options.successNotification) {
            dispatch(addNotification({ message: options.successNotification }));
        }
    } catch (err) {
        dispatch(addToCartAction.failure({ error: err as Error, identifier }));
        log404ErrorAsWarning(err, 'ADD_TO_CART', logger, 'addToCart');
        if (options.errorNotification) {
            dispatch(addNotification({ message: options.errorNotification, displayType: 'error' }));
        }
    }
};

export const cartActions = [
    ...splitAsyncAction(fetchCartDataAction),
    ...splitAsyncAction(fetchCartCountAction),
    ...splitAsyncAction(addToCartAction),
    clearCartAction,
    clearCartDataAction,
    markCountFetched,
];

export type CartActionType = ActionType<
    | typeof fetchCartDataAction
    | typeof fetchCartCountAction
    | typeof addToCartAction
    | typeof clearCartAction
    | typeof clearCartDataAction
    | typeof markCountFetched
>;
